import { animated, config, useSpring } from '@react-spring/web';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

import { CookieCategoryType, CookieConfig } from '../../api/types/cookies';

interface CookieBarProps {
    data: CookieConfig['config'];
    onSetup: () => void;
    allow: (categories?: string[]) => void;
}

function CookieBar({ data, onSetup, allow }: CookieBarProps) {
    const { t, i18n } = useTranslation('global', { keyPrefix: 'cookies' });

    const styles = useSpring({
        from: {
            maxHeight: 0,
        },
        to: {
            maxHeight: 700,
        },
        config: { ...config.default, duration: 1000 },
    });

    if (!data || !i18n.resolvedLanguage) return null;

    return (
        <animated.div
            style={styles}
            className="cookie-bar fixed bottom-0 left-0 z-10 w-full bg-white px-4 py-6 normal-case sm:top-auto sm:h-auto sm:px-16">
            <div
                dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                        data[i18n.resolvedLanguage].consent,
                    ),
                }}
            />

            <div className="mt-4 flex flex-col flex-wrap lg:flex-row lg:flex-nowrap">
                <div className="text-center">
                    <button
                        onClick={() =>
                            allow(
                                data.categoryTypes
                                    .filter(
                                        (cT: CookieCategoryType) => cT.active,
                                    )
                                    .map((cT: CookieCategoryType) => cT.id),
                            )
                        }
                        type="button"
                        className="font-xl btn-hover mb-2 mr-6 uppercase tracking-[3.8px]">
                        <span className="hover-right inline-block pr-4 text-xl">
                            →
                        </span>
                        <span>{t('allowAll')}</span>
                    </button>
                </div>

                <div className="text-center">
                    <button
                        onClick={() => allow()}
                        type="button"
                        className="font-xl btn-hover mb-2 pr-4 uppercase tracking-[3.8px]">
                        <span className="hover-right inline-block pr-4 text-xl">
                            →
                        </span>
                        <span>{t('disallowAll')}</span>
                    </button>
                </div>

                <div className="lg:align-right text-center lg:grow">
                    <button
                        type="button"
                        onClick={onSetup}
                        className="font-xl btn-hover pr-4 uppercase tracking-[3.8px] lg:float-right">
                        <span>{t('setup')}</span>
                        <span className="hover-down inline-block pl-4 text-xl">
                            ↓
                        </span>
                    </button>
                </div>
            </div>
        </animated.div>
    );
}

export default CookieBar;
